import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Zap, Filter, Search } from 'lucide-react';
import AutomationCard, { AutomationFlow } from '../components/automations/AutomationCard';
import AutomationBuilder from '../components/automations/AutomationBuilder';

export default function Automations() {
  const navigate = useNavigate();
  const [showBuilder, setShowBuilder] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'paused' | 'error'>('all');

  // Sample automation flows
  const [automations, setAutomations] = useState<AutomationFlow[]>([
    {
      id: '1',
      name: 'Daily Analytics Report',
      description: 'Automatically generate and email daily analytics reports',
      trigger: {
        type: 'schedule',
        label: 'Daily at 9:00 AM'
      },
      actions: [
        { type: 'database', label: 'Fetch Analytics Data' },
        { type: 'transform', label: 'Generate Report' },
        { type: 'email', label: 'Send Email' }
      ],
      status: 'active',
      lastRun: '2h ago',
      runCount: 47
    },
    {
      id: '2',
      name: 'New User Onboarding',
      description: 'Welcome new users with automated email sequence',
      trigger: {
        type: 'event',
        label: 'On User Signup'
      },
      actions: [
        { type: 'email', label: 'Send Welcome Email' },
        { type: 'notification', label: 'Notify Team' },
        { type: 'database', label: 'Update User Profile' }
      ],
      status: 'active',
      lastRun: '15m ago',
      runCount: 234
    },
    {
      id: '3',
      name: 'Weekly Data Backup',
      description: 'Export and backup all system data weekly',
      trigger: {
        type: 'schedule',
        label: 'Every Sunday at 2:00 AM'
      },
      actions: [
        { type: 'database', label: 'Export Database' },
        { type: 'export', label: 'Create Backup File' },
        { type: 'notification', label: 'Notify Admin' }
      ],
      status: 'paused',
      lastRun: '3d ago',
      runCount: 12
    },
    {
      id: '4',
      name: 'Error Alert System',
      description: 'Monitor and alert on system errors',
      trigger: {
        type: 'event',
        label: 'On Error Event'
      },
      actions: [
        { type: 'notification', label: 'Send Alert' },
        { type: 'email', label: 'Email Dev Team' }
      ],
      status: 'error',
      lastRun: '1h ago',
      runCount: 8
    },
    {
      id: '5',
      name: 'API Data Sync',
      description: 'Sync data with external APIs every hour',
      trigger: {
        type: 'schedule',
        label: 'Every Hour'
      },
      actions: [
        { type: 'api', label: 'Fetch External Data' },
        { type: 'transform', label: 'Transform Data' },
        { type: 'database', label: 'Update Records' }
      ],
      status: 'active',
      lastRun: '45m ago',
      runCount: 156
    }
  ]);

  const handleToggle = (id: string) => {
    setAutomations(automations.map(auto =>
      auto.id === id
        ? { ...auto, status: auto.status === 'active' ? 'paused' : 'active' }
        : auto
    ));
  };

  const handleEdit = (id: string) => {
    // TODO: Implement edit functionality
    console.log('Edit automation:', id);
  };

  const handleDelete = (id: string) => {
    setAutomations(automations.filter(auto => auto.id !== id));
  };

  const handleSaveAutomation = (automation: {
    name: string;
    description: string;
    trigger: { type: string; label: string };
    actions: Array<{ type: string; label: string }>;
  }) => {
    const newAutomation: AutomationFlow = {
      id: Date.now().toString(),
      name: automation.name,
      description: automation.description,
      trigger: automation.trigger as AutomationFlow['trigger'],
      actions: automation.actions,
      status: 'active' as const,
      runCount: 0
    };
    setAutomations([newAutomation, ...automations]);
  };

  const filteredAutomations = automations
    .filter(auto => {
      const matchesSearch = auto.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           auto.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = filterStatus === 'all' || auto.status === filterStatus;
      return matchesSearch && matchesFilter;
    });

  const stats = {
    total: automations.length,
    active: automations.filter(a => a.status === 'active').length,
    paused: automations.filter(a => a.status === 'paused').length,
    error: automations.filter(a => a.status === 'error').length
  };

  return (
    <div className="min-h-screen bg-recipe-bg">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-recipe-card border-b border-recipe-border backdrop-blur-sm bg-opacity-95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-recipe-text hover:text-recipe-accent transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back to Dashboard</span>
            </button>

            <button
              onClick={() => setShowBuilder(true)}
              className="flex items-center gap-2 px-4 py-2 bg-recipe-accent text-recipe-bg rounded-lg hover:bg-recipe-accent/90 transition-all hover:scale-105 font-semibold"
            >
              <Plus className="w-4 h-4" />
              Create Automation
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8 animate-fade-in">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-3 bg-recipe-accent/10 rounded-xl">
              <Zap className="w-8 h-8 text-recipe-accent" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-recipe-text">Automation Flows</h1>
              <p className="text-recipe-text-muted">Create and manage automated workflows</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-recipe-card border border-recipe-border rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-recipe-text-muted mb-1">Total Automations</p>
                <p className="text-2xl font-bold text-recipe-text">{stats.total}</p>
              </div>
              <div className="p-3 bg-recipe-bg rounded-lg">
                <Zap className="w-6 h-6 text-recipe-accent" />
              </div>
            </div>
          </div>

          <div className="bg-recipe-card border border-recipe-border rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-recipe-text-muted mb-1">Active</p>
                <p className="text-2xl font-bold text-green-500">{stats.active}</p>
              </div>
              <div className="p-3 bg-green-500/10 rounded-lg">
                <Zap className="w-6 h-6 text-green-500" />
              </div>
            </div>
          </div>

          <div className="bg-recipe-card border border-recipe-border rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-recipe-text-muted mb-1">Paused</p>
                <p className="text-2xl font-bold text-recipe-text-muted">{stats.paused}</p>
              </div>
              <div className="p-3 bg-recipe-bg rounded-lg">
                <Zap className="w-6 h-6 text-recipe-text-muted" />
              </div>
            </div>
          </div>

          <div className="bg-recipe-card border border-recipe-border rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-recipe-text-muted mb-1">Errors</p>
                <p className="text-2xl font-bold text-red-500">{stats.error}</p>
              </div>
              <div className="p-3 bg-red-500/10 rounded-lg">
                <Zap className="w-6 h-6 text-red-500" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters & Search */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-recipe-text-muted" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search automations..."
              className="w-full pl-10 pr-4 py-2.5 bg-recipe-card border border-recipe-border rounded-lg text-recipe-text focus:outline-none focus:ring-2 focus:ring-recipe-accent/50"
            />
          </div>

          {/* Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-recipe-text-muted" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as typeof filterStatus)}
              className="px-4 py-2.5 bg-recipe-card border border-recipe-border rounded-lg text-recipe-text focus:outline-none focus:ring-2 focus:ring-recipe-accent/50"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="error">Error</option>
            </select>
          </div>
        </div>

        {/* Automations List */}
        {filteredAutomations.length > 0 ? (
          <div className="grid grid-cols-1 gap-4">
            {filteredAutomations.map((automation) => (
              <AutomationCard
                key={automation.id}
                automation={automation}
                onToggle={handleToggle}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        ) : (
          <div className="bg-recipe-card border border-recipe-border rounded-xl p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-recipe-bg rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-recipe-text-muted" />
              </div>
              <h3 className="text-lg font-semibold text-recipe-text mb-2">
                {searchTerm || filterStatus !== 'all' ? 'No automations found' : 'No automations yet'}
              </h3>
              <p className="text-recipe-text-muted mb-6">
                {searchTerm || filterStatus !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'Create your first automation to streamline your workflow'}
              </p>
              {!searchTerm && filterStatus === 'all' && (
                <button
                  onClick={() => setShowBuilder(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-recipe-accent text-recipe-bg rounded-lg hover:bg-recipe-accent/90 transition-all font-semibold"
                >
                  <Plus className="w-5 h-5" />
                  Create Your First Automation
                </button>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Automation Builder Modal */}
      {showBuilder && (
        <AutomationBuilder
          onClose={() => setShowBuilder(false)}
          onSave={handleSaveAutomation}
        />
      )}
    </div>
  );
}
